import * as React from 'react';
import { IgcTileComponent as Component } from 'igniteui-webcomponents';
import type { IgcTileComponentEventMap } from 'igniteui-webcomponents';
import { type EventName, createComponent } from '../react-props.js';

/**
 * The tile component is used within the `igc-tile-manager` as a container
 * for displaying various types of information.
 * @slot  - Default slot for the tile's content.
 * @slot title - Renders the title of the tile header.
 * @slot maximize-action - Renders the maximize action element of the tile header.
 * @slot fullscreen-action - Renders the fullscreen action element of the tile header.
 * @slot actions - Renders items after the default actions in the tile header.
 * @slot side-adorner - Renders the side resize handle of the tile.
 * @slot corner-adorner - Renders the corner resize handle of the tile.
 * @slot bottom-adorner - Renders the bottom resize handle of the tile.
 * @fires igcTileFullscreen - Fired when tile the fullscreen state changes.
 * @fires igcTileMaximize - Fired when tile the maximize state changes.
 * @fires igcTileDragStart - Fired when a drag operation on a tile is about to begin. Cancelable.
 * @fires igcTileDragEnd - Fired when a drag operation with a tile is successfully completed.
 * @fires igcTileDragCancel - Fired when a tile drag operation is canceled by the user.
 * @fires igcTileResizeStart - Fired when a resize operation on a tile is about to begin. Cancelable.
 * @fires igcTileResizeEnd - Fired when a resize operation on a tile is successfully completed.
 * @fires igcTileResizeCancel - Fired when a resize operation on a tile is canceled by the user.
 * @csspart base - The wrapper for the entire tile content, header and content.
 * @csspart header - The container for the tile header, including title and actions.
 * @csspart title - The title container of the tile.
 * @csspart actions - The actions container of the tile header.
 * @csspart content-container - The container wrapping the tile’s main content.
 * @csspart trigger-side - The part for the side adorner of the encapsulated resize element in the tile.
 * @csspart trigger - The part for the corner adorner of the encapsulated resize element in the tile.
 * @csspart trigger-bottom - The part for the bottom adorner of the encapsulated resize element in the tile.
 * @class
 */
export const IgrTile = /* @__PURE__ */ createComponent({
  tagName: 'igc-tile',
  displayName: 'IgrTile',
  elementClass: Component,
  react: React,
  events: {
    onTileFullscreen: 'igcTileFullscreen' as EventName<
      IgcTileComponentEventMap['igcTileFullscreen']
    >,
    onTileMaximize: 'igcTileMaximize' as EventName<
      IgcTileComponentEventMap['igcTileMaximize']
    >,
    onTileDragStart: 'igcTileDragStart' as EventName<
      IgcTileComponentEventMap['igcTileDragStart']
    >,
    onTileDragEnd: 'igcTileDragEnd' as EventName<
      IgcTileComponentEventMap['igcTileDragEnd']
    >,
    onTileDragCancel: 'igcTileDragCancel' as EventName<
      IgcTileComponentEventMap['igcTileDragCancel']
    >,
    onTileResizeStart: 'igcTileResizeStart' as EventName<
      IgcTileComponentEventMap['igcTileResizeStart']
    >,
    onTileResizeEnd: 'igcTileResizeEnd' as EventName<
      IgcTileComponentEventMap['igcTileResizeEnd']
    >,
    onTileResizeCancel: 'igcTileResizeCancel' as EventName<
      IgcTileComponentEventMap['igcTileResizeCancel']
    >,
  },
});

export type IgrTile = Component;

/** @deprecated Module register is no longer needed and can be removed */
export const IgrTileModule = Component;
